import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { api } from '@/lib/api/client'
import { isApiError } from '@/lib/api/errors'
import { asList } from '@/lib/api/list'
import { useAuth } from '@/lib/auth/AuthProvider'
import type {
  AccountInput,
  AccountMovement,
  AccountPeriodSummary,
  AccountType,
  CardBrand,
  CashEntryInput,
  CashFlow,
  Category,
  CategoryInput,
  Counterparty,
  CounterpartyInput,
  EDocInput,
  EDocStatus,
  EDocType,
  EDocument,
  Obligation,
  ObligationInput,
  ObligationItem,
  ObligationKind,
  ObligationPayInput,
  ObligationSummary,
  FinAccount,
  Money,
  Overview,
  PayStatus,
  Payment,
  PaymentChannel,
  PaymentInput,
  ReviewStatus,
  TaxParams,
  TaxReport,
  Transaction,
  TransactionInput,
  TransferInput,
  TxKind,
  VatStatus,
} from './types'
import { ACCOUNT_TYPES, CARD_BRANDS, EDOC_STATUSES, EDOC_TYPES, OBLIGATION_KINDS, PAYMENT_CHANNELS, RECURRENCES, REVIEW_STATUSES, TX_KINDS, VAT_STATUSES } from './types'

export const financeKeys = {
  all: ['finance'] as const,
  accounts: ['finance', 'accounts'] as const,
  movements: ['finance', 'movements'] as const,
  categories: ['finance', 'categories'] as const,
  parties: ['finance', 'parties'] as const,
  transactions: ['finance', 'transactions'] as const,
  payments: ['finance', 'payments'] as const,
  edocs: ['finance', 'edocs'] as const,
  taxParams: ['finance', 'tax-params'] as const,
  obligations: ['finance', 'obligations'] as const,
  overview: (m: string) => ['finance', 'overview', m] as const,
  cashFlow: (m: string) => ['finance', 'cash-flow', m] as const,
  tax: (m: string) => ['finance', 'tax', m] as const,
  audit: ['finance', 'audit'] as const,
  accountSummary: (m: string) => ['finance', 'account-summary', m] as const,
}

type Row = Record<string, unknown>

function rowsOf(data: unknown): Row[] {
  if (Array.isArray(data)) return data as Row[]
  if (data && typeof data === 'object') {
    const obj = data as Row
    for (const key of ['items', 'data', 'results', 'accounts', 'parties', 'categories', 'entries', 'ledger', 'documents', 'payments', 'journals', 'rows']) {
      if (Array.isArray(obj[key])) return obj[key] as Row[]
    }
  }
  return asList(data as Row[] | { items?: Row[] })
}

function str(v: unknown, fallback = '') {
  return v == null ? fallback : String(v)
}
function num(v: unknown) {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
function money(v: unknown) {
  if (typeof v === 'number' && Number.isFinite(v)) return v.toFixed(2)
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v)
    return Number.isFinite(n) ? n.toFixed(2) : '0.00'
  }
  return '0.00'
}
function cents(v: string): bigint {
  const neg = v.trim().startsWith('-')
  const [whole, frac = ''] = v.replace('-', '').split('.')
  const value = BigInt(whole || '0') * 100n + BigInt((frac + '00').slice(0, 2) || '0')
  return neg ? -value : value
}
function fromCents(n: bigint) {
  const neg = n < 0n
  const v = neg ? -n : n
  return `${neg ? '-' : ''}${v / 100n}.${(v % 100n).toString().padStart(2, '0')}`
}
function sumMoney(values: string[]) {
  return fromCents(values.reduce((n, v) => n + cents(v), 0n))
}
function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  const s = String(v ?? '')
  return (allowed as readonly string[]).includes(s) ? (s as T) : fallback
}
function nullableNum(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function txKind(v: unknown): TxKind {
  const s = String(v ?? '')
  if (s === 'income' || s === 'gelir') return 'income'
  return oneOf(v, TX_KINDS, 'expense')
}
function partyKind(v: unknown): 'supplier' | 'customer' {
  const s = String(v ?? '')
  if (s === 'customer' || s === 'musteri' || s === 'müşteri') return 'customer'
  return 'supplier'
}
function reviewOf(v: unknown): ReviewStatus {
  const s = String(v ?? '')
  if (s === 'approved' || s === 'onaylandi' || s === 'approved') return 'approved'
  if (s === 'reviewing' || s === 'incelemede') return 'reviewing'
  return oneOf(v, REVIEW_STATUSES, 'pending')
}
function payStatus(v: unknown, paid: string, total: string): PayStatus {
  const s = String(v ?? '')
  if (s === 'paid' || s === 'partial' || s === 'unpaid') return s
  const p = cents(paid)
  const t = cents(total)
  if (p <= 0n) return 'unpaid'
  if (p >= t) return 'paid'
  return 'partial'
}
function channelOf(v: unknown): PaymentChannel {
  const s = String(v ?? '')
  if (s === 'kart' || s === 'card') return 'card'
  if (s === 'nakit' || s === 'cash') return 'cash'
  if (s === 'pos') return 'pos'
  return oneOf(v, PAYMENT_CHANNELS, 'transfer')
}
function channelBody(c: PaymentChannel) {
  if (c === 'card') return 'kart'
  if (c === 'cash') return 'nakit'
  if (c === 'pos') return 'pos'
  return 'havale'
}
function cardBrandOf(v: unknown): CardBrand | '' {
  const s = String(v ?? '')
  return (CARD_BRANDS as readonly string[]).includes(s) ? (s as CardBrand) : ''
}
function accountType(v: unknown): AccountType {
  const s = String(v ?? '')
  if (s === 'kasa') return 'cash'
  if (s === 'kredi' || s === 'card') return 'credit_card'
  return oneOf(v, ACCOUNT_TYPES, 'bank')
}
function vatOf(v: unknown): VatStatus {
  return oneOf(v, VAT_STATUSES, 'eligible')
}
function edocType(v: unknown): EDocType {
  const s = String(v ?? '')
  if (s === 'e_arsiv' || s === 'e_archive') return 'e_archive'
  return oneOf(v, EDOC_TYPES, 'e_invoice')
}
function edocStatus(v: unknown): EDocStatus {
  const s = String(v ?? '')
  if (s === 'gonderildi') return 'sent'
  if (s === 'kabul') return 'accepted'
  if (s === 'red') return 'rejected'
  if (s === 'iptal') return 'cancelled'
  return oneOf(v, EDOC_STATUSES, 'draft')
}

function mapAccount(row: Row): FinAccount {
  const type = accountType(row.type ?? row.account_type)
  return {
    id: str(row.id),
    name: str(row.name ?? row.title),
    type,
    bankName: str(row.bank_name ?? row.bankName),
    bankId: str(row.bank_id),
    bankLogoUrl: str(row.bank_logo_url),
    currency: str(row.currency) || 'TRY',
    balanceTry: str(row.balance_try),
    fxRate: str(row.fx_rate),
    fxRateDate: str(row.fx_rate_date),
    branch: str(row.branch),
    accountNo: str(row.account_no ?? row.accountNo),
    ibanMasked: str(row.iban_masked ?? row.ibanMasked),
    cardBrand: cardBrandOf(row.card_brand ?? row.cardBrand),
    cardHolder: str(row.card_holder ?? row.cardHolder),
    cardLast4: str(row.card_last4 ?? row.cardLast4 ?? row.last4),
    cardExpiry: str(row.card_expiry ?? row.cardExpiry),
    cardLimit: money(row.card_limit ?? row.cardLimit),
    cashAdvanceLimit: money(row.cash_advance_limit),
    statementDay: nullableNum(row.statement_day ?? row.statementDay),
    dueDay: nullableNum(row.due_day ?? row.dueDay),
    merchantId: str(row.merchant_id ?? row.merchantId),
    openingBalance: money(row.opening_balance ?? row.openingBalance ?? row.opening),
    active: row.active !== false && row.is_active !== false,
    balance: money(row.balance),
    availableLimit:
      row.available_limit == null && row.availableLimit == null
        ? type === 'credit_card'
          ? null
          : null
        : money(row.available_limit ?? row.availableLimit),
    settlementMode: row.settlement_mode === 'auto' ? 'auto' : 'manual',
    settlementDays: nullableNum(row.settlement_days),
    commissionRate: str(row.commission_rate),
    settlementAccountId: str(row.settlement_account_id),
  }
}

function mapCategory(row: Row): Category {
  return {
    id: str(row.id),
    name: str(row.name ?? row.title),
    kind: txKind(row.kind ?? row.direction),
    vatStatus: vatOf(row.vat_eligibility ?? row.vat_status ?? row.vatStatus),
    color: num(row.color) || 1,
    active: row.active !== false && row.is_active !== false,
  }
}

function mapParty(row: Row): Counterparty {
  return {
    id: str(row.id),
    name: str(row.title ?? row.name),
    kind: partyKind(row.party_type ?? row.kind),
    taxNo: str(row.vkn ?? row.tax_no ?? row.taxNo),
    taxOffice: str(row.tax_office ?? row.taxOffice),
    bankName: str(row.bank_name ?? row.bankName),
    ibanMasked: str(row.iban_masked ?? row.ibanMasked),
    email: str(row.email),
    phone: str(row.phone),
  }
}

function mapTransaction(row: Row): Transaction {
  const net = money(row.amount_ex_vat ?? row.net ?? row.amount)
  const vat = money(row.vat)
  const withholding = money(row.withholding ?? row.withholding_amount)
  const total = money(row.total ?? row.amount_inc_vat ?? net)
  const paid = money(row.paid ?? row.paid_amount)
  const remaining = row.remaining == null ? fromCents(cents(total) - cents(paid)) : money(row.remaining)
  return {
    id: str(row.id),
    kind: txKind(row.kind),
    counterpartyId: str(row.party_id ?? row.counterparty_id ?? row.counterpartyId),
    categoryId: str(row.category_id ?? row.categoryId),
    docNo: str(row.doc_no ?? row.docNo),
    date: str(row.doc_day ?? row.doc_date ?? row.date).slice(0, 10),
    dueDate: str(row.due_date ?? row.dueDate).slice(0, 10),
    description: str(row.description),
    net,
    vatRate: num(row.vat_rate ?? row.vatRate),
    withholdingRate: num(row.withholding_rate ?? row.withholdingRate),
    hasDocument: row.document_present === true || row.has_document === true || row.hasDocument === true,
    review: reviewOf(row.review ?? row.review_status),
    accountantNote: str(row.review_note ?? row.accountant_note ?? row.accountantNote),
    departmentId: str(row.department_id),
    vat,
    withholding,
    total,
    paid,
    remaining,
    status: payStatus(row.status ?? row.pay_status, paid, total),
  }
}

function mapPayment(row: Row): Payment {
  return {
    id: str(row.id),
    transactionId: str(row.ledger_entry_id ?? row.transaction_id ?? row.transactionId),
    accountId: str(row.money_account_id ?? row.account_id ?? row.accountId),
    date: str(row.date ?? row.paid_at).slice(0, 10),
    amount: money(row.amount),
    channel: channelOf(row.method ?? row.channel),
    reference: str(row.reference ?? row.ref_code),
    note: str(row.note),
    hasReceipt: row.has_receipt === true || row.hasReceipt === true,
  }
}

function mapEDoc(row: Row): EDocument {
  return {
    id: str(row.id),
    type: edocType(row.doc_kind ?? row.type),
    direction: String(row.direction ?? '') === 'gelen' || String(row.direction ?? '') === 'incoming' ? 'incoming' : 'outgoing',
    number: str(row.number ?? row.doc_no),
    ettn: str(row.ettn),
    counterpartyId: str(row.party_id ?? row.counterpartyId),
    transactionId: row.ledger_entry_id == null && row.transaction_id == null ? null : str(row.ledger_entry_id ?? row.transaction_id),
    date: str(row.doc_date ?? row.date).slice(0, 10),
    total: money(row.amount ?? row.total),
    status: edocStatus(row.status),
    statusNote: str(row.status_note ?? row.statusNote),
  }
}

function mapMovement(row: Row): AccountMovement {
  const direction = String(row.direction ?? '') === 'out' || String(row.direction ?? '') === 'cikis' ? 'out' : 'in'
  const source = String(row.source ?? '')
  return {
    id: str(row.id),
    date: str(row.date).slice(0, 10),
    accountId: str(row.money_account_id ?? row.account_id ?? row.accountId),
    description: str(row.description ?? row.note),
    amount: money(row.amount),
    direction,
    source: source === 'transfer' || source === 'entry' || source === 'payment' ? source : 'entry',
    refId: str(row.ref_id ?? row.refId),
    createdBy: str(row.created_by_name),
    createdAt: str(row.created_at),
    voided: row.voided === true,
    voidedAt: str(row.voided_at),
    voidedBy: str(row.voided_by_name),
    voidRequestedBy: str(row.void_requested_by_name),
    voidReason: str(row.void_reason),
    voidDecisionNote: str(row.void_decision_note),
  }
}

function emptyTrend(value = '0.00') {
  return { value, changePct: null as number | null }
}
function trend(v: unknown, fallback = '0.00') {
  if (v && typeof v === 'object') {
    const row = v as Row
    const change = row.change_pct ?? row.changePct
    return { value: money(row.value ?? fallback), changePct: change == null ? null : num(change) }
  }
  if (v == null) return emptyTrend(fallback)
  return { value: money(v), changePct: null }
}

function mapCashFlow(period: string, raw: unknown): CashFlow {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Row
  const months = rowsOf(row.months ?? row.series ?? raw).map((m) => ({
    month: str(m.month ?? m.period, period),
    inflow: money(m.inflow ?? m.in),
    outflow: money(m.outflow ?? m.out),
    net: money(m.net),
    closing: money(m.closing ?? m.balance),
  }))
  const projected = (row.projected ?? {}) as Row
  return {
    months: months.length ? months : [{ month: period, inflow: '0.00', outflow: '0.00', net: '0.00', closing: money(row.cash ?? row.balance) }],
    upcoming: rowsOf(row.upcoming).map((u) => ({
      transactionId: str(u.transaction_id ?? u.ledger_entry_id ?? u.transactionId),
      kind: txKind(u.kind),
      dueDate: str(u.due_date ?? u.dueDate).slice(0, 10),
      remaining: money(u.remaining ?? u.amount),
      overdue: u.overdue === true,
    })),
    projected: {
      inflow: money(projected.inflow),
      outflow: money(projected.outflow),
      closing: money(projected.closing ?? row.cash),
    },
    cash: money(row.cash ?? row.balance ?? projected.closing),
  }
}

function mapTax(period: string, raw: unknown): TaxReport {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Row
  const vat = (row.vat ?? {}) as Row
  const review = (row.review ?? {}) as Row
  const withholding = (row.withholding ?? {}) as Row
  const corporate = (row.corporate ?? {}) as Row
  const doc = (row.doc_check ?? row.docCheck ?? {}) as Row
  return {
    period: str(row.period, period),
    vat: {
      output: trend(vat.output),
      input: trend(vat.input),
      carried: money(vat.carried),
      payable: money(vat.payable),
      reconciliationPct: num(vat.reconciliation_pct ?? vat.reconciliationPct),
    },
    monthly: rowsOf(row.monthly).map((m) => ({ month: str(m.month), output: money(m.output), input: money(m.input), payable: money(m.payable) })),
    review: { approved: num(review.approved), pending: num(review.pending), problem: num(review.problem) },
    withholding: {
      total: money(withholding.total),
      rows: rowsOf(withholding.rows).map((r) => ({ transactionId: str(r.transaction_id ?? r.ledger_entry_id ?? r.transactionId), base: money(r.base), amount: money(r.amount) })),
    },
    corporate: {
      base: money(corporate.base),
      estimate: money(corporate.estimate),
      ytdIncome: money(corporate.ytd_income ?? corporate.ytdIncome),
      ytdExpense: money(corporate.ytd_expense ?? corporate.ytdExpense),
      interestIncome: money(corporate.interest_income),
      gross: money(corporate.gross),
      prepaidTax: money(corporate.prepaid_tax),
      rate: str(corporate.rate),
    },
    docCheck: { missing: num(doc.missing), missingAmount: money(doc.missing_amount ?? doc.missingAmount), unreviewed: num(doc.unreviewed) },
    vatRows: rowsOf(row.vat_rows ?? row.vatRows).map((r) => ({ transactionId: str(r.transaction_id ?? r.ledger_entry_id ?? r.transactionId), vatStatus: vatOf(r.vat_status ?? r.vatStatus) })),
  }
}

function mapOverview(period: string, flowRaw: unknown, ledgerRaw: unknown): Overview {
  const flow = mapCashFlow(period, flowRaw)
  const flowRow = (flowRaw && typeof flowRaw === 'object' ? flowRaw : {}) as Row
  const entries = rowsOf(ledgerRaw).map(mapTransaction).filter((t) => !t.date || t.date.startsWith(period))
  const income = entries.filter((t) => t.kind === 'income')
  const expense = entries.filter((t) => t.kind === 'expense')
  const byCat = new Map<string, { amount: bigint; documented: bigint; missing: bigint; count: number; missingCount: number; reviews: ReviewStatus[] }>()
  for (const tx of expense) {
    const cur = byCat.get(tx.categoryId) ?? { amount: 0n, documented: 0n, missing: 0n, count: 0, missingCount: 0, reviews: [] }
    const amount = cents(tx.total)
    cur.amount += amount
    cur.count += 1
    cur.reviews.push(tx.review)
    if (tx.hasDocument) cur.documented += amount
    else {
      cur.missing += amount
      cur.missingCount += 1
    }
    byCat.set(tx.categoryId, cur)
  }
  const expenseTotal = sumMoney(expense.map((t) => t.total))
  const expenseCents = cents(expenseTotal)
  const categories = [...byCat.entries()].map(([categoryId, v]) => ({
    categoryId,
    amount: fromCents(v.amount),
    pct: expenseCents === 0n ? 0 : Number((v.amount * 10000n) / expenseCents) / 100,
  }))
  const receivable = income.filter((t) => t.status !== 'paid')
  const payable = expense.filter((t) => t.status !== 'paid')
  const months = new Map<string, { income: bigint; expense: bigint }>()
  for (const tx of entries) {
    const key = tx.date.slice(0, 7) || period
    const cur = months.get(key) ?? { income: 0n, expense: 0n }
    if (tx.kind === 'income') cur.income += cents(tx.total)
    else cur.expense += cents(tx.total)
    months.set(key, cur)
  }
  return {
    period,
    collected: trend(flowRow.collected, sumMoney(income.map((t) => t.paid))),
    paidOut: trend(flowRow.paid_out ?? flowRow.paidOut, sumMoney(expense.map((t) => t.paid))),
    netCash: trend(flowRow.net_cash ?? flowRow.netCash, flow.months.at(-1)?.net ?? '0.00'),
    receivable: {
      value: money(flowRow.receivable ?? sumMoney(receivable.map((t) => t.remaining))),
      parties: new Set(receivable.map((t) => t.counterpartyId).filter(Boolean)).size,
      docs: receivable.length,
    },
    payable: {
      value: money((flowRow.payable as Row | undefined)?.value ?? flowRow.payable ?? sumMoney(payable.map((t) => t.remaining))),
      parties: new Set(payable.map((t) => t.counterpartyId).filter(Boolean)).size,
      docs: payable.length,
    },
    monthly: [...months.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, v]) => ({ month, income: fromCents(v.income), expense: fromCents(v.expense) })),
    categories,
    expenseTotal,
    accrual: {
      income: trend(flowRow.accrual && (flowRow.accrual as Row).income, sumMoney(income.map((t) => t.total))),
      expense: trend(flowRow.accrual && (flowRow.accrual as Row).expense, expenseTotal),
      result: emptyTrend(fromCents(cents(sumMoney(income.map((t) => t.total))) - cents(expenseTotal))),
    },
    tax: { vat: '0.00', withholding: '0.00', corporate: '0.00' },
    missingDocs: expense.filter((t) => !t.hasDocument).length,
    categoryDetail: [...byCat.entries()].map(([categoryId, v]) => {
      const unique = new Set(v.reviews)
      return {
        categoryId,
        amount: fromCents(v.amount),
        documented: fromCents(v.documented),
        missing: fromCents(v.missing),
        count: v.count,
        missingCount: v.missingCount,
        review: unique.size === 1 ? [...unique][0]! : 'mixed',
      }
    }),
  }
}

function accountBody(input: AccountInput) {
  return {
    name: input.name,
    type: input.type,
    bank_name: input.bankName,
    bank_id: input.type === 'cash' ? '' : input.bankId,
    currency: input.currency || 'TRY',
    branch: input.branch,
    account_no: input.accountNo,
    iban: input.iban,
    clear_iban: input.clearIban === true,
    card_brand: input.cardBrand,
    card_holder: input.cardHolder,
    card_last4: input.cardLast4,
    card_expiry: input.cardExpiry,
    card_limit: input.cardLimit,
    cash_advance_limit: input.type === 'bank' ? input.cashAdvanceLimit : '',
    statement_day: input.statementDay,
    due_day: input.dueDay,
    merchant_id: input.merchantId,
    opening_balance: input.openingBalance,
    active: input.active,
    ...(input.type === 'pos'
      ? {
          settlement_mode: input.settlementMode,
          settlement_days: input.settlementMode === 'auto' ? input.settlementDays : null,
          commission_rate: input.commissionRate,
          settlement_account_id: input.settlementAccountId,
        }
      : {}),
  }
}
function partyBody(input: CounterpartyInput) {
  return {
    party_type: input.kind === 'customer' ? 'musteri' : 'tedarikci',
    title: input.name,
    vkn: input.taxNo,
    tax_office: input.taxOffice,
    email: input.email,
    phone: input.phone,
    iban: input.iban,
    clear_iban: input.clearIban === true,
  }
}
function ledgerBody(input: TransactionInput) {
  return {
    kind: input.kind === 'income' ? 'gelir' : 'gider',
    category_id: input.categoryId,
    party_id: input.counterpartyId,
    doc_no: input.docNo,
    doc_date: input.date,
    due_date: input.dueDate,
    description: input.description,
    amount_ex_vat: input.net,
    vat_rate: String(input.vatRate),
    withholding_rate: String(input.withholdingRate),
    document_present: input.hasDocument,
    review_status: input.review,
    review_note: input.accountantNote,
  }
}

/** Departman yeni kayıtta yalnız seçildiyse gönderilir (boşsa kategorinin varsayılanı uygulanır); güncellemede her zaman. */
const withDepartment = (body: Record<string, unknown>, departmentId: string, update: boolean) =>
  update || departmentId ? { ...body, department_id: departmentId } : body

function useInvalidate() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: financeKeys.all })
}
const useMut = <A, R = unknown>(fn: (arg: A) => Promise<R>) => {
  const invalidate = useInvalidate()
  return useMutation({ mutationFn: fn, onSuccess: invalidate })
}

const list = async (path: string, query?: Record<string, string>) => rowsOf(await api.get<unknown>(path, query))

export const useAccounts = () => useQuery({ queryKey: financeKeys.accounts, queryFn: async () => (await list('/api/v1/finance/money-accounts')).map(mapAccount) })
/** Tahsilat/ödeme, transfer ve kasa hareketleri tek listede (iptal edilenler hariç). */
export const useMovements = () => useQuery({ queryKey: financeKeys.movements, queryFn: async () => (await list('/api/v1/finance/movements')).map(mapMovement) })
export const useCategories = () => useQuery({ queryKey: financeKeys.categories, queryFn: async () => (await list('/api/v1/finance/categories')).map(mapCategory) })
export const useParties = () => useQuery({ queryKey: financeKeys.parties, queryFn: async () => (await list('/api/v1/finance/parties')).map(mapParty) })
export const useTransactions = () => useQuery({ queryKey: financeKeys.transactions, queryFn: async () => (await list('/api/v1/finance/ledger')).map(mapTransaction) })
export const usePayments = () => useQuery({ queryKey: financeKeys.payments, queryFn: async () => (await list('/api/v1/finance/payments')).map(mapPayment) })
export const useEDocs = () => useQuery({ queryKey: financeKeys.edocs, queryFn: async () => (await list('/api/v1/finance/e-documents')).map(mapEDoc) })
export const useTaxParams = () =>
  useQuery({
    queryKey: financeKeys.taxParams,
    queryFn: async (): Promise<TaxParams> => {
      const row = (await api.get<Row>('/api/v1/finance/tax-params')) ?? {}
      return {
        vatRates: Array.isArray(row.vat_rates) ? row.vat_rates.map(num) : [],
        withholdingRate: num(row.withholding_rate),
        corporateRate: num(row.corporate_rate),
        depreciationYears: num(row.depreciation_years),
        updatedAt: str(row.updated_at),
      }
    },
  })
export const useOverview = (m: string) =>
  useQuery({
    queryKey: financeKeys.overview(m),
    queryFn: async () => {
      const [flow, ledger, tax] = await Promise.all([
        api.get<unknown>('/api/v1/finance/cash-flow', { period: m }),
        api.get<unknown>('/api/v1/finance/ledger'),
        api.get<unknown>('/api/v1/finance/tax/preview', { period: m }),
      ])
      const overview = mapOverview(m, flow, ledger)
      const preview = mapTax(m, tax)
      overview.tax = { vat: preview.vat.payable, withholding: preview.withholding.total, corporate: preview.corporate.estimate }
      return overview
    },
  })
export const useCashFlow = (m: string) => useQuery({ queryKey: financeKeys.cashFlow(m), queryFn: async () => mapCashFlow(m, await api.get<unknown>('/api/v1/finance/cash-flow', { period: m })) })
/** Dönem özeti hesap ve hareket önbelleklerinden türetilir: kapanış = güncel bakiye − dönem sonrası net. */
export function useAccountSummary(m: string) {
  const accounts = useAccounts()
  const movements = useMovements()
  const data = useMemo(() => {
    if (!accounts.data || !movements.data) return undefined
    return accounts.data.map((account): AccountPeriodSummary => {
      const own = movements.data.filter((x) => x.accountId === account.id && !x.voided)
      const signed = (x: AccountMovement) => (x.direction === 'in' ? cents(x.amount) : -cents(x.amount))
      const after = own.filter((x) => x.date.slice(0, 7) > m).reduce((n, x) => n + signed(x), 0n)
      const inPeriod = own.filter((x) => x.date.startsWith(m))
      const inflow = inPeriod.filter((x) => x.direction === 'in').reduce((n, x) => n + cents(x.amount), 0n)
      const outflow = inPeriod.filter((x) => x.direction === 'out').reduce((n, x) => n + cents(x.amount), 0n)
      const closing = cents(account.balance) - after
      return { accountId: account.id, opening: fromCents(closing - inflow + outflow), inflow: fromCents(inflow), outflow: fromCents(outflow), closing: fromCents(closing), count: inPeriod.length }
    })
  }, [accounts.data, movements.data, m])
  return { data, isLoading: accounts.isLoading || movements.isLoading }
}
export const fetchTaxReport = async (m: string) => mapTax(m, await api.get<unknown>('/api/v1/finance/tax/preview', { period: m }))
export const useTaxReport = (m: string) => useQuery({ queryKey: financeKeys.tax(m), queryFn: () => fetchTaxReport(m) })

export const useJournals = (period: string) => useQuery({ queryKey: ['finance', 'journals', period], queryFn: () => api.get<unknown>('/api/v1/finance/journals', { period }) })
export const useJournal = (id: string) => useQuery({ queryKey: ['finance', 'journals', id], queryFn: () => api.get<unknown>(`/api/v1/finance/journals/${id}`), enabled: !!id })
export const usePayrollMirrors = (period: string) => useQuery({ queryKey: ['finance', 'payroll-mirrors', period], queryFn: () => api.get<unknown>('/api/v1/finance/payroll-mirrors', { period }) })
export const useCostSummary = (projectId: string) =>
  useQuery({ queryKey: ['finance', 'cost-summary', projectId], queryFn: () => api.get<unknown>('/api/v1/finance/cost-summary', { project_id: projectId }), enabled: !!projectId })

export const useSaveAccount = () =>
  useMut(({ id, input }: { id?: string; input: AccountInput }) => (id ? api.put(`/api/v1/finance/money-accounts/${id}`, accountBody(input)) : api.post('/api/v1/finance/money-accounts', accountBody(input))))
export const useDeleteAccount = () => useMut((id: string) => api.del(`/api/v1/finance/money-accounts/${id}`))
export const useTransfer = () =>
  useMut((input: TransferInput) =>
    api.post('/api/v1/finance/transfers', {
      from_account_id: input.fromId,
      to_account_id: input.toId,
      amount: input.amount,
      note: input.note,
      ...(input.commissionRate != null && input.commissionRate !== '' ? { commission_rate: input.commissionRate } : {}),
      ...(input.toAmount ? { to_amount: input.toAmount } : {}),
    }),
  )
/**
 * Çıkan ödeme sonucu: doğrudan ödeme yetkisi yoksa sunucu PAYMENT_APPROVAL_REQUIRED döner; kullanıcı talep açabiliyorsa
 * aynı ödeme onay talebi olarak gönderilir (sahip/ortaklar onaylayınca işlenir).
 */
export type PayResult = { pendingApproval: boolean }

function useApprovalFallback() {
  const { can } = useAuth()
  return async (
    direct: () => Promise<unknown>,
    kind: 'cash_out' | 'ledger_payment' | 'obligation_pay',
    payload: unknown,
    reason = '',
  ): Promise<PayResult> => {
    try {
      await direct()
      return { pendingApproval: false }
    } catch (e) {
      if (!isApiError(e) || e.code !== 'PAYMENT_APPROVAL_REQUIRED' || !can('finance:payment_request:create')) throw e
      await api.post('/api/v1/finance/payment-requests', { kind, payload, reason })
      return { pendingApproval: true }
    }
  }
}

/** Silme muhasebede ters kayıttır: hareket iptal edilir, yevmiyesi ters çevrilir. */
export const useSaveCashEntry = () => {
  const pay = useApprovalFallback()
  return useMut((input: CashEntryInput) => {
    const body = {
      amount: input.amount,
      date: input.date,
      description: input.description,
      category_id: input.categoryId ?? '',
      reference: input.reference,
      ...(input.fxRate ? { fx_rate: input.fxRate.replace(',', '.') } : {}),
      ...(input.departmentId ? { department_id: input.departmentId } : {}),
    }
    const direct = () =>
      api.post(
        `/api/v1/finance/money-accounts/${input.accountId}/cash-${input.direction === 'in' ? 'in' : 'out'}`,
        body,
      )
    return pay(direct, 'cash_out', { ...body, account_id: input.accountId }, input.description)
  })
}
export const useSaveCategory = () =>
  useMut(({ id, input }: { id?: string; input: CategoryInput }) => {
    const body = { kind: input.kind === 'income' ? 'gelir' : 'gider', name: input.name, color: String(input.color), vat_eligibility: input.vatStatus, is_active: input.active }
    return id ? api.put(`/api/v1/finance/categories/${id}`, body) : api.post('/api/v1/finance/categories', body)
  })
/** Kullanılan kategori pasife alınır, kullanılmayan silinir. */
export const useDeleteCategory = () => useMut((id: string) => api.del(`/api/v1/finance/categories/${id}`))
export const useSaveParty = () =>
  useMut(async ({ id, input }: { id?: string; input: CounterpartyInput }) => {
    const saved = id ? await api.put<Row>(`/api/v1/finance/parties/${id}`, partyBody(input)) : await api.post<Row>('/api/v1/finance/parties', partyBody(input))
    return mapParty({ ...partyBody(input), id, ...saved })
  })
/** Tam IBAN sunucudan alınır; her gösterim denetim kaydına yazılır (önbelleğe alınmaz). */
export function useRevealIban() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ target, id }: { target: 'account' | 'party'; id: string; actor: string }) =>
      str((await api.post<Row>(`/api/v1/finance/${target === 'account' ? 'money-accounts' : 'parties'}/${id}/reveal-iban`, {})).iban),
    onSuccess: () => qc.invalidateQueries({ queryKey: financeKeys.audit }),
  })
}
export const useAudit = () =>
  useQuery({
    queryKey: financeKeys.audit,
    queryFn: async () =>
      (await list('/api/v1/finance/sensitive-audit')).map((row) => ({
        id: str(row.id),
        at: str(row.at),
        by: str(row.by),
        target: (row.target === 'party' ? 'party' : 'account') as 'account' | 'party',
        targetId: str(row.target_id),
        field: 'iban' as const,
      })),
  })
export const useSaveTransaction = () =>
  useMut(async ({ id, input }: { id?: string; input: TransactionInput }) => {
    const body = withDepartment(ledgerBody(input), input.departmentId ?? '', !!id)
    const saved = id ? await api.put<Row>(`/api/v1/finance/ledger/${id}`, body) : await api.post<Row>('/api/v1/finance/ledger', body)
    return mapTransaction({ ...ledgerBody(input), id, ...saved })
  })
export const useApproveLedger = () => useMut((id: string) => api.post(`/api/v1/finance/ledger/${id}/approve`, {}))
export const useSavePayment = () => {
  const pay = useApprovalFallback()
  return useMut((input: PaymentInput) => {
    const body = {
      ledger_entry_id: input.transactionId,
      money_account_id: input.accountId,
      amount: input.amount,
      method: channelBody(input.channel),
    }
    return pay(() => api.post('/api/v1/finance/payments', body), 'ledger_payment', body)
  })
}
export function useCreateEDoc() {
  const qc = useQueryClient()
  return useMut(async (input: EDocInput) => {
    let partyId = ''
    let amount = '0.00'
    let docDate = new Date().toISOString().slice(0, 10)
    if (input.transactionId) {
      // Kayıt önbellekte yoksa tek seferlik liste çekilir.
      const txs = qc.getQueryData<Transaction[]>(financeKeys.transactions) ?? (await list('/api/v1/finance/ledger')).map(mapTransaction)
      const hit = txs.find((x) => x.id === input.transactionId)
      if (hit) {
        partyId = hit.counterpartyId
        amount = hit.total
        docDate = hit.date || docDate
      }
    }
    return api.post('/api/v1/finance/e-documents', {
      doc_kind: input.type === 'e_archive' ? 'e_arsiv' : 'e_fatura',
      direction: 'giden',
      party_id: partyId,
      amount,
      doc_date: docDate,
    })
  })
}
export const useSetEDocStatus = () =>
  useMut(({ id, status, note }: { id: string; status: EDocStatus; note?: string }) =>
    status === 'sent' ? api.post(`/api/v1/finance/e-documents/${id}/send`, {}) : api.patch(`/api/v1/finance/e-documents/${id}/status`, { status, note: note ?? '' }),
  )
export const useClosePeriod = () => useMut((period: string) => api.post(`/api/v1/finance/periods/${period}/close`, {}))
export const useSaveTaxParams = () =>
  useMut((input: Omit<TaxParams, 'updatedAt'>) =>
    api.put('/api/v1/finance/tax-params', { vat_rates: input.vatRates, withholding_rate: input.withholdingRate, corporate_rate: input.corporateRate, depreciation_years: input.depreciationYears }),
  )

/* ---- Ödeme planı ---- */

function mapObligation(row: Row): Obligation {
  return {
    id: str(row.id),
    title: str(row.title),
    kind: oneOf(row.kind, OBLIGATION_KINDS, 'other'),
    direction: row.direction === 'in' ? 'in' : 'out',
    categoryId: str(row.category_id),
    partyId: str(row.party_id),
    accountId: str(row.money_account_id),
    departmentId: str(row.department_id),
    recurrence: oneOf(row.recurrence, RECURRENCES, 'monthly'),
    dueDay: num(row.due_day),
    startDate: str(row.start_date),
    endDate: str(row.end_date),
    periodOffset: num(row.period_offset_months),
    vatRate: num(row.vat_rate),
    withholdingRate: num(row.withholding_rate),
    remindDays: Array.isArray(row.remind_days) ? row.remind_days.map(num) : [],
    notes: str(row.notes),
    source: row.source === 'payroll' ? 'payroll' : 'manual',
    active: row.active !== false,
    currentAmount: money(row.current_amount),
    nextDue: str(row.next_due),
    rates: rowsOf(row.rates ?? []).map((r) => ({ id: str(r.id), validFrom: str(r.valid_from), amount: money(r.amount), note: str(r.note) })),
  }
}

function mapObligationItem(row: Row): ObligationItem {
  return {
    id: str(row.id),
    obligationId: str(row.obligation_id),
    title: str(row.title),
    kind: oneOf(row.kind, OBLIGATION_KINDS, 'other'),
    direction: row.direction === 'in' ? 'in' : 'out',
    period: str(row.period),
    dueDate: str(row.due_date),
    amount: money(row.amount),
    locked: row.amount_locked === true,
    status: row.status === 'paid' ? 'paid' : row.status === 'skipped' ? 'skipped' : 'pending',
    overdue: row.overdue === true,
    daysLeft: num(row.days_left),
    note: str(row.note),
    accountId: str(row.money_account_id),
    ledgerEntryId: str(row.ledger_entry_id),
    paidOn: str(row.paid_on),
  }
}

function obligationBody(input: ObligationInput) {
  return {
    title: input.title,
    kind: input.kind,
    direction: input.direction,
    category_id: input.categoryId,
    party_id: input.partyId,
    money_account_id: input.accountId,
    recurrence: input.recurrence,
    due_day: input.dueDay,
    start_date: input.startDate,
    end_date: input.endDate,
    period_offset_months: input.periodOffset,
    vat_rate: String(input.vatRate),
    withholding_rate: String(input.withholdingRate),
    remind_days: input.remindDays,
    notes: input.notes,
    active: input.active,
    department_id: input.departmentId,
    ...(input.amount ? { amount: input.amount } : {}),
  }
}

export const useObligations = () =>
  useQuery({ queryKey: [...financeKeys.obligations, 'list'], queryFn: async () => (await list('/api/v1/finance/obligations')).map(mapObligation) })
/** Oran geçmişi yalnız düzenleme açıkken istenir. */
export const useObligation = (id: string) =>
  useQuery({ queryKey: [...financeKeys.obligations, 'detail', id], enabled: !!id, queryFn: async () => mapObligation((await api.get<Row>(`/api/v1/finance/obligations/${id}`)) ?? {}) })
export const useObligationItems = (q: { from?: string; to?: string; kind?: string } = {}) =>
  useQuery({
    queryKey: [...financeKeys.obligations, 'items', q.from ?? '', q.to ?? '', q.kind ?? ''],
    queryFn: async () =>
      (await list('/api/v1/finance/obligation-items', Object.fromEntries(Object.entries(q).filter(([, v]) => !!v)) as Record<string, string>)).map(mapObligationItem),
  })
export const useObligationSummary = () =>
  useQuery({
    queryKey: [...financeKeys.obligations, 'summary'],
    queryFn: async (): Promise<ObligationSummary> => {
      const row = (await api.get<Row>('/api/v1/finance/obligations/summary')) ?? {}
      return {
        overdueCount: num(row.overdue_count),
        overdueAmount: money(row.overdue_amount),
        monthOut: money(row.month_out),
        monthIn: money(row.month_in),
        monthPaid: money(row.month_paid),
        next30Out: money(row.next30_out),
        next30In: money(row.next30_in),
        next7Count: num(row.next7_count),
      }
    },
  })

/** Tanım/oran değişikliği yalnız ödeme planı sorgularını yeniler; ödeme ise kayıt ve bakiyeleri de etkiler. */
function useObligationMut<A, R = unknown>(fn: (arg: A) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: financeKeys.obligations }) })
}
export const useSaveObligation = () =>
  useObligationMut(async ({ id, input }: { id?: string; input: ObligationInput }) =>
    mapObligation((id ? await api.put<Row>(`/api/v1/finance/obligations/${id}`, obligationBody(input)) : await api.post<Row>('/api/v1/finance/obligations', obligationBody(input))) ?? {}),
  )
export const useDeleteObligation = () => useObligationMut((id: string) => api.del<{ deleted?: boolean }>(`/api/v1/finance/obligations/${id}`))
export const useSaveObligationRate = () =>
  useObligationMut(({ id, validFrom, amount, note }: { id: string; validFrom: string; amount: string; note: string }) =>
    api.post(`/api/v1/finance/obligations/${id}/rates`, { valid_from: validFrom, amount, note }),
  )
export const useDeleteObligationRate = () =>
  useObligationMut(({ id, rateId }: { id: string; rateId: string }) => api.del(`/api/v1/finance/obligations/${id}/rates/${rateId}`))
export const useSkipObligationItem = () =>
  useObligationMut(({ id, skip, note }: { id: string; skip: boolean; note?: string }) =>
    api.post(`/api/v1/finance/obligation-items/${id}/${skip ? 'skip' : 'unskip'}`, { note: note ?? '' }),
  )
/** Sistem öncesi dönemden tek seferlik ödeme/tahsilat: tanım açılır ve kalemi ödeme tarihiyle ödenir. */
export type PastPaymentInput = {
  title: string
  kind: ObligationKind
  direction: 'out' | 'in'
  categoryId: string
  partyId: string
  period: string
  dueDate: string
  paidOn: string
  amount: Money
  accountId: string
  channel: PaymentChannel
  docNo: string
  note: string
}
export const usePastPayment = () =>
  useMut((input: PastPaymentInput) =>
    api.post('/api/v1/finance/obligations/past-payment', {
      title: input.title.trim(),
      kind: input.kind,
      direction: input.direction,
      category_id: input.categoryId,
      party_id: input.partyId,
      period: input.period,
      due_date: input.dueDate || input.paidOn,
      paid_on: input.paidOn,
      amount: input.amount,
      money_account_id: input.accountId,
      method: channelBody(input.channel),
      doc_no: input.docNo,
      note: input.note,
    }),
  )
/** Tanımın vadesi geçmiş bekleyen kalemlerini her biri kendi vade tarihinde ödenmiş olarak işler. */
export const usePayPastItems = () =>
  useMut(({ id, accountId, until, channel }: { id: string; accountId: string; until: string; channel: PaymentChannel }) =>
    api.post<{ paid?: number }>(`/api/v1/finance/obligations/${id}/pay-past`, { money_account_id: accountId, until, method: channelBody(channel) }),
  )
export const usePayObligationItem = () => {
  const pay = useApprovalFallback()
  return useMut(({ id, input }: { id: string; input: ObligationPayInput }) => {
    const body = {
      money_account_id: input.accountId,
      paid_on: input.paidOn,
      amount: input.amount,
      method: channelBody(input.channel),
      doc_no: input.docNo,
      note: input.note,
    }
    return pay(
      () => api.post(`/api/v1/finance/obligation-items/${id}/pay`, body),
      'obligation_pay',
      { item_id: id, input: body },
      input.note,
    )
  })
}
